import { gql, useMutation } from "@apollo/client";

import {
  Comment,
  CreateCommentInput,
  UpdateCommentInput,
} from "../../../generated/gql/graphql";

const CREATE_COMMENT_MUTATION = gql`
  mutation CreateComment($input: CreateCommentInput!) {
    createComment(input: $input) {
      id
      status
      sort
      assetId
      parentId
      comment
      dateCreated
      dateUpdated
    }
  }
`;

const DELETE_COMMENT_MUTATION = gql`
  mutation DeleteComment($deleteCommentId: String!) {
    deleteComment(id: $deleteCommentId)
  }
`;

const UPDATE_COMMENT_MUTATION = gql`
  mutation updateComment(
    $updateCommentId: String!
    $input: UpdateCommentInput!
  ) {
    updateComment(id: $updateCommentId, input: $input) {
      id
      status
      sort
      assetId
      parentId
      comment
      dateCreated
      dateUpdated
    }
  }
`;

type AssetCommentMutationApi = {
  createComment(input: CreateCommentInput): Promise<Comment>;
  deleteComment(id: string): Promise<void>;
  updateComment(id: string, input: UpdateCommentInput): Promise<Comment>;
};

export default function useAssetCommentMutation(): AssetCommentMutationApi {
  const [createCommentMut] = useMutation<{ createComment: Comment }>(
    CREATE_COMMENT_MUTATION
  );
  const [deleteCommentMut] = useMutation(DELETE_COMMENT_MUTATION);
  const [updateCommentMut] = useMutation(UPDATE_COMMENT_MUTATION);

  async function createComment(input: CreateCommentInput): Promise<Comment> {
    try {
      const result = await createCommentMut({
        variables: {
          input,
        },
      });

      if (result.errors || !result.data) {
        console.error(result.errors);
        return Promise.reject(
          new Error(`Failed to create comment for asset ${input.assetId}.`)
        );
      }

      return result.data?.createComment;
    } catch (error) {
      console.error(error);
      throw new Error(`Failed to create a comment for asset ${input.assetId}.`);
    }
  }

  async function deleteComment(id: string): Promise<void> {
    if (!id) {
      throw new Error("Comment id is required.");
    }

    try {
      await deleteCommentMut({
        variables: {
          deleteCommentId: id,
        },
      });
    } catch (error) {
      console.error(error);
      throw new Error(`Failed to delete a comment ${id}.`);
    }
  }

  async function updateComment(
    id: string,
    input: UpdateCommentInput
  ): Promise<Comment> {
    try {
      const result = await updateCommentMut({
        variables: {
          updateCommentId: id,
          input: input,
        },
      });

      if (result.errors || !result.data) {
        console.error(result.errors);
        return Promise.reject(new Error(`Failed to update comment ${id}.`));
      }

      return result.data?.updateComment;
    } catch (error) {
      console.error(error);
      throw new Error(`Failed to update a comment ${id}.`);
    }
  }

  return {
    createComment,
    deleteComment,
    updateComment,
  };
}
